import { EntityTag } from "@/apidtos";
import { AuthGuard } from "@core/components/AuthGuard";
import { Alert, AlertDescription } from "@core/components/ui/alert";
import { Button } from "@core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@core/components/ui/dialog";
import { useCurrentCart, UseCurrentCartOptions } from "@ecom/hooks/cartHooks";
import { useMedicalConfig } from "@phoenix/med/hooks/useMedicalConfig";
import { DialogDescription, DialogProps } from "@radix-ui/react-dialog";
import { Link } from "@remix-run/react";
import { t } from "i18next";
import React from "react";
import {
  Form,
  FormProvider,
  useForm
} from "react-hook-form";
import { CustomCompoundFields } from "./CustomCompoundFields";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";

interface CustomCompoundTags {
  Therapy: EntityTag;
  Medication: EntityTag;
  MedicationName?: EntityTag;
  AllowSubstitutions?: EntityTag;
}

export const CustomCompoundDialog = (
  props: DialogProps
) => {
  const medConfig = useMedicalConfig();
  const useCurrentCartOptions: UseCurrentCartOptions = {
    refetchOnWindowFocus: false,
  };
  const { addItems } = useCurrentCart("ShoppingCart", undefined, useCurrentCartOptions);
  const [ open, setOpen ] = React.useState(false);
  const [ complete, setComplete ] = React.useState(false);
  const form = useForm<CustomCompoundTags>({
    defaultValues: {
      Therapy: { Key: "Therapy", Value: ""},
      Medication: { Key: "Medication", Value: ""},
      MedicationName: { Key: "MedicationName", Value: ""},
      AllowSubstitutions: { Key: "AllowSubstitutions", Value: ""},
    },
    mode: "onChange",
  });
  if (!medConfig.CustomCompoundTherapies?.length) {
    return null;
  }

  function handleSubmit(data: CustomCompoundTags) {
    const tagsToAdd = Object.keys(data)
      .filter(key => !!data[key as keyof CustomCompoundTags]?.Value)
      .map(key => ({
        Key: key,
        Value: data[key as keyof CustomCompoundTags]?.Value
      }));
    let forceUniqueLineItemKey = "CustomCompound";
    if (data.Therapy.Value && data.Therapy.Value.length > 0) {
      forceUniqueLineItemKey += `|${data.Therapy.Value}`;
    }
    if (data.Medication.Value && data.Medication.Value.length > 0) {
      const medValue =
        data.Medication.Value === "Other"
          ? data.MedicationName!.Value
          : data.Medication.Value;
      forceUniqueLineItemKey += `|${medValue}`;
    }
    addItems.mutate([
      {
        Quantity: 1,
        Key: "CustomCompound",
        Tags: tagsToAdd,
        ForceUniqueLineItemKey: forceUniqueLineItemKey,
      },
    ]);
    setComplete(true);
  }

  return (
    <Dialog
      {...props}
      open={open}
      onOpenChange={() => setOpen(x => {
        if (x) {
          setComplete(false);
          form.reset();
        }
        return !x;
      })}
      aria-describedby="Custom Compound Modal"
    >
      {props.children}
      <AuthGuard requiresAnyRole={["Provider", "Global Admin"]}>
        {!props.children && (
          <DialogTrigger asChild>
            <Button type="button" variant="outlinePrimary">
              {t("ui.CreateCustomCompound", "Create Custom Compound")}
            </Button>
          </DialogTrigger>
        )}
      </AuthGuard>
      <DialogContent
        className="w-full flex flex-col max-h-[98vh]"
        aria-describedby="Custom Compound Modal"
      >
        <DialogHeader>
          <DialogTitle>
            {t("ui.CustomCompoundModal", "Custom Compound Modal")}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">
          {t(
            "UseThisFormToCreateCustomCompound",
            "Use this form to create a custom compound."
          )}
        </DialogDescription>
        {complete
          ? (
            <div className="flex flex-col gap-4 align-end">
              <Alert variant="success" className="mt-4 text-dark">
                <AlertDescription>
                  {t("ui.CustomCompoundCreated", "Custom Compound Created!")}{" "}
                  {t("ui.productAddedToCart", "Product added to the cart")}.
                </AlertDescription>
              </Alert>
              <Link to="/cart" className="w-0">
                <Button>
                  {t("ui.ViewCart", "View Cart")}
                </Button>
              </Link>
            </div>
          )
          : (
            <FormProvider {...form}>
              <Form<CustomCompoundTags> control={form.control} onSubmit={({ data }) => handleSubmit(data)}>
                <CustomCompoundFields />
                <Button
                  type="submit"
                  className="w-full text-sm mb-2 gap-1"
                  disabled={!form.formState.isValid}
                >
                  {t("ui.CreateCustomCompound", "Create Custom Compound")}
                </Button>
              </Form>
            </FormProvider>
          )}
      </DialogContent>
    </Dialog>
  );
};

registerExtension("CatalogHead.FirstItem", CustomCompoundDialog, "CustomCompoundDialog");
